import React, { FC } from 'react';
import moment from 'moment';
import { Popover } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { nullSafeGet } from '../../utils/DataAccessUtils';

/**
 * `13 March` over `2026`, as the design prints it.
 *
 * Deliberately NOT `dateFormatter` + `DATE_ONLY_FORMAT`: that renders through the viewer's zone, and
 * an `occurredAt` stored at UTC midnight would show as the previous day. On a record whose whole
 * purpose is "when was this appliance serviced" that is a wrong answer, not a cosmetic one — so the
 * instant is read in UTC, the zone it was recorded against.
 *
 * The year sits on its own line rather than wrapping by accident, so a narrow column cannot break
 * `13 March 2026` into `13` / `March 2026` and read as a different date.
 */
export const RefrigerantEventDate: FC<{ occurredAt: any }> = ({ occurredAt }) => {
	if (!occurredAt) return <span className="text-character-secondary">--</span>;
	const utcMoment = moment.utc(occurredAt);
	return (
		<div className="flex flex-col">
			<span className="bodyregular text-character-primary">{utcMoment.format('D MMMM')}</span>
			<span className="footnoteregular text-character-secondary">{utcMoment.format('YYYY')}</span>
		</div>
	);
};

/**
 * `EPA Type II 5510-114` from the event's certification SNAPSHOT.
 *
 * Read off the event and never off the technician's contact record: a cert renewed or corrected next
 * year must not rewrite what last year's 608 record attests to. Renders `--` when the snapshot is
 * absent rather than falling back to anything live, for the same reason.
 */
export const formatEventCertification = (event: any): string => {
	const certificationType = nullSafeGet('certificationType', event);
	const certificationNumber = nullSafeGet('certificationNumber', event);
	const parts = [certificationType, certificationNumber].filter(Boolean);
	return parts.length > 0 ? parts.join(' ') : '--';
};

/**
 * The (i) beside a ledger row's technician, carrying the certification that row attests to.
 *
 * The working ledger deliberately has no Certification COLUMN — the same tech recurs on almost every
 * row of an appliance's record, so a column of near-identical certificate numbers costs width and
 * tells the reader nothing they were looking for. Only the printable servicing record prints it, where
 * it is the thing that makes the document evidence. But "which certificate was this event logged
 * against" is still a question somebody asks of one row at a time, and until this existed the only way
 * to answer it was to open the servicing record.
 *
 * Reads the event's own SNAPSHOT through `formatEventCertification`, never the live contact, for the
 * reason that helper documents: a cert renewed next year must not rewrite what last year's record
 * attests to. Rendered as nothing at all when the row carries no snapshot — an icon that opens to
 * `--` is an invitation to check something that is not there.
 *
 * Popover rather than Tooltip: a certificate class and number is content the reader may want to select
 * and copy against a document in their other hand, and a tooltip disappears the moment the pointer
 * leaves the icon.
 */
export const RefrigerantEventCertificationInfo: FC<{ event: any }> = ({ event }) => {
	const certification = formatEventCertification(event);
	if (certification === '--') return null;
	return (
		<Popover
			content={
				<div className="flex flex-col gap-2xs" style={{ maxWidth: 260 }}>
					<span className="footnoteregular text-character-secondary">
						EPA 608 certification recorded on this event
					</span>
					<span className="bodymedium text-character-primary">{certification}</span>
					{/* Says WHY the number may not match the tech's card today. Without it, a reader who
					    checks the credential against the contact record and finds a renewed number reads
					    the difference as an error rather than as the point of a snapshot. */}
					<span className="footnoteregular text-character-secondary">
						Recorded when the event was logged, so a later renewal does not change it.
					</span>
				</div>
			}
		>
			<InfoCircleOutlined className="text-character-secondary" />
		</Popover>
	);
};
